import React, { Component } from "react";
import { compose } from "recompose";
import time from "src/utils/time";
import { withTranslation } from "react-i18next";
import LazyLoad from "react-lazyload";
import withEventAudioPlayer from "src/modules/activity-log/enhancers/withEventAudioPlayer";
import StartEvent from "src/modules/activity-log/components/StartEvent";
import AwakeEvent from "src/modules/activity-log/components/AwakeEvent";
import SleepingEvent from "src/modules/activity-log/components/SleepingEvent";
import CaptureEvent from "src/modules/activity-log/components/CaptureEvent";
import Line from "src/modules/activity-log/components/Line";
import {
  eventsFilters,
  isMediaEvent
} from "src/modules/activity-log/supported-events";
import EndEvent from "./EndEvent";
import withStartTimeColumnWidth from "src/modules/activity-log/enhancers/withStartTimeColumnWidth";
import ScrollBox from "src/atoms/ScrollBox";

function filterEvent(event) {
  return eventsFilters[event.eventType]?.(event) || false;
}

function getLineHeight(eventType, nextEventType, isParentEvent) {
  if (isParentEvent) {
    return "50px";
  }
  if (isMediaEvent(eventType) && isMediaEvent(nextEventType)) {
    return "120px";
  }
  if (isMediaEvent(eventType) || isMediaEvent(nextEventType)) {
    return "72px";
  }
  return "24px";
}

function getGroupedEvents(events) {
  return events.reduce((array, event) => {
    if (array.includes(event)) return array;
    if (!event.referenceId?.value) {
      array.push(event);
      return array;
    }
    events.forEach((filteredEvent, i) => {
      if (event.referenceId?.value === filteredEvent.id) {
        if (array.includes(filteredEvent)) {
          array.splice(i, 1);
        }
        array.push(filteredEvent);
        array.push(event);
      }
    });
    return array;
  }, []);
}

class Timeline extends Component {
  scrollBoxRef = React.createRef();

  componentDidMount() {
    this.scrollToTop();
  }
  componentDidUpdate(prevProps, prevState) {
    if (
      prevProps.monitoring.monitoringId === this.props.monitoring.monitoringId
    )
      return;
    this.props.eventAudioPlayer && this.props.eventAudioPlayer.stop();
    this.scrollToTop();
  }
  componentWillUnmount() {
    this.props.eventAudioPlayer.stop();
  }

  scrollToTop = () => this.scrollBoxRef.current.scrollTo(0, 0);
  render() {
    const {
      monitoring,
      avatar,
      eventAudioPlayer,
      startTimeColumnWidth,
      openEventView,
      toggleFreemiumFlow,
      fetchEventThumbnail
    } = this.props;

    const { startDate, events, freemium, duration } = monitoring;
    const lineMarginLeft = `${startTimeColumnWidth + 25}px`;
    const filteredEvents = Object.values(events).filter(filterEvent);
    filteredEvents.sort((a, b) =>
      a.start > b.start ? 1 : b.start > a.start ? -1 : 0
    );

    const groupedEvents = getGroupedEvents(filteredEvents);

    return (
      <ScrollBox reference={this.scrollBoxRef} height="100vh">
        <StartEvent
          startDate={startDate}
          startTimeColumnWidth={startTimeColumnWidth}
          activityLogType="history"
        />
        <Line
          ml={lineMarginLeft}
          mt="10px"
          height={getLineHeight(null, groupedEvents[0]?.eventType)}
        />
        {groupedEvents.map((event, index) => {
          const commonProps = {
            event,
            startTimeColumnWidth,
            avatar,
            activityLogType: "history",
            formattedStartTime: time(startDate).add(event.start).format("LT"),
            hasParent: false,
            isParent: false
          };

          if (event.id === groupedEvents[index + 1]?.referenceId?.value) {
            commonProps.isParent = true;
          }
          if (
            event.referenceId?.value &&
            event.referenceId.value === groupedEvents[index - 1]?.id
          ) {
            commonProps.hasParent = true;
          }

          return (
            <LazyLoad
              key={event.id}
              overflow
              once
              height={36}
              unmountIfInvisible
            >
              <React.Fragment>
                {event.eventType === "AWAKE" && (
                  <AwakeEvent
                    toggleFreemiumFlow={toggleFreemiumFlow}
                    eventAudioPlayer={eventAudioPlayer}
                    freemium={freemium}
                    {...commonProps}
                  />
                )}
                {event.eventType === "SLEEPING" && (
                  <SleepingEvent {...commonProps} />
                )}
                {isMediaEvent(event.eventType) && (
                  <CaptureEvent
                    {...commonProps}
                    openEventView={() => {
                      openEventView(event.id);
                      eventAudioPlayer.stop();
                    }}
                    fetchEventThumbnail={fetchEventThumbnail}
                  />
                )}

                <Line
                  height={getLineHeight(
                    event.eventType,
                    groupedEvents[index + 1]?.eventType,
                    commonProps.isParent
                  )}
                  ml={lineMarginLeft}
                  position="relative"
                />
              </React.Fragment>
            </LazyLoad>
          );
        })}
        <EndEvent
          startTimeColumnWidth={startTimeColumnWidth}
          startDate={startDate}
          duration={duration}
        />
      </ScrollBox>
    );
  }
}

const widestEventStartTime = time().set({ minute: 59, hour: 12 }).format("LT");

const enhance = compose(
  withStartTimeColumnWidth({ startTime: widestEventStartTime, maxWidth: 80 }),
  withEventAudioPlayer,
  withTranslation()
);

export default enhance(Timeline);
